import axios, { AxiosResponse } from "axios";

import {
	getRequest,
	postRequest,
	putRequest,
	deleteRequest
} from "@/Utils/Service/BackendService";
import {
	concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse,
	deepFreeze,
	getFileNameFromContentDisposition,
	withNormalizedDocumentCurrencyConfig
} from "@/Utils";
import {
	Block,
	BlockResponse,
	DeletedRecord,
	ZomentumEntities
} from "@/Contracts";
import {
	Document,
	DocumentsRequest,
	DocumentsResponse,
	AddDocumentRequest,
	MailRecipientResponse,
	EditDocumentRequest,
	DocumentExpiry,
	EmptyMergeTagResponse,
	DownloadDocumentProductListRequest,
	DocumentPayment,
	ClientPaymentData,
	MergeTagInput,
	MergeTagValueUpdateResponse,
	DocumentBulkUpdateStatusRequest,
	DynamicFormCreateRequest,
	DocumentApprovalSendRequest,
	DocumentApprovalVerdictRequest,
	DocumentPinGenerationRequest,
	VerifyDocumentPinRequest,
	VerifyDocumentPinResponse,
	DocumentPinGenerationResponse,
	ConnectBoosterPaymentRequest,
	UpdatePoDetailsPayload,
	BulkAssignSignatureBlocksRequest,
	PaymentSummary,
	PaymentMethod,
	QuickbooksDocumentSyncFormData,
	QuickbooksSyncFormDataParams,
	UpdateOrderFormPublishStatusRequest,
	SubmitOrderFormRequest,
	OrderFormRequestorDetailsToClientDetailsRequest,
	UpdateDocumentLinkStatusRequest,
	UserType,
	OpenAiResponse,
	OpenAIQueryHistoryResponse,
	QuickbooksCreateCustomerRequest,
	SendDocumentForSignatureRequest,
	AIWriterTrailResponse,
	AutotaskProductValidationView,
	RecentDocumentRequest,
	RecentDocumentResponse
} from "@/Contracts/Documents";
import { getDocumentPinCache } from "@/V2Utils/Documents";
import {
	getTableQueryFilters,
	includeChildEntities,
	ChildEntityHydrationParams,
	getQueryParamAuthDetails,
	QueryParamConstants,
	QueryParamCredentials
} from "@/Contracts/Utils";
import {
	ApprovalDetails,
	ApprovalStatusResponse,
	DocumentRevision,
	DocumentPageDetails,
	DocumentWonUrlResponse
} from "@/Contracts/Documents/Revision";
import {
	CustomViewsMetadata,
	CustomViewResponse,
	CustomView,
	ListPagePostRequest,
	BulkDeleteRecordsRequest,
	BulkUpdateRecordsRequest
} from "@/Contracts/UI";
import { QuickbooksCustomerResponse } from "@/Contracts/Settings";
import { Gateway } from "@/Contracts";
import { CommonNameValuePair } from "@/Contracts";
import { AIDocumentCreateResponse } from "@/Components/UI/CreateDocumentModal/interface";
import { UpdatePoDetailsResponse } from "@/Contracts/Documents";

const getDocumentsUrl = "/v2/documents";
const recentDocumentURL = "/v2/documents/recent/";
const deleteDocumentUrl = "/v2/documents/{document_id}";
const addDocumentUrl = "/v2/documents";
const editDocumentUrl = "/v2/documents/{document_id}";
const editRevisionBlockListUrl = "/v2/documents/reorder/{document_id}";
const editRevisionUrl = "/v2/documents/{document_id}/revisions/{revision_id}";
const updateDocumentPageDetailsUrl =
	"/v2/documents/update-page-details/{document_id}";
const getDocumentDetailsUrl = "/v2/documents/{document_id}";
const setDocumentPaymentSettings = "/v2/documents/{document_id}/payment";
const updateClientPaymentSettings =
	"/v2/documents/{document_id}/update-payment";
const startPayment = "/v2/documents/{document_id}/start-payment";
const getMailRecipientsUrl = "/v2/client/users?limit=5000";
const downloadDocumentUrl = "/v2/documents/download/{document_id}";
const approveDocumentUrl = "/v2/documents/approve/{document_id}";
const cancelSignatureDocumentUrl =
	"/v2/documents/cancel-signature/{document_id}";
const enableEditingUrl = "/v2/documents/{document_id}/enable-editing";
const enableClientViewingUrl =
	"/v2/documents/{document_id}/enable-client-viewing";
const assignSignatureBlockToRecipientUrl = "/v2/documents/assign/{document_id}";
const reorderSignersUrl = "/v2/documents/reorder-signers/{document_id}";
const renewDocumentUrl = "/v2/documents/expired/renew/{document_id}/";
const getEmptyMergeTagsUrl = "/v2/documents/{document_id}/merge_tags/validate";
const validateAutotaskItemsURL =
	"/v2/documents/{document_id}/autotask_items/validate";
const downloadDocumentProductListUrl =
	"/v2/documents/{document_id}/revisions/{revision_id}/downloadproducts/";
const getDocumentPaymentStatus = "/v2/connect-booster/redirect/";
const documentBulkUpdateUrl = "/v2/documents/bulk/update/";
const postPushChangesToAutotask = "/v2/documents/export-to-psa/:id";
const getAllDocumentViewURL = "/v2/views?entity_type={entity_type}";
const getDocumentViewMetadataURL =
	"/v2/views/metadata?entity_type={entity_type}";

const updateMergeTagValues = "/v2/merge_tags";

const getDocumentListPostURL = "/v2/documents/view";
const createDocumentViewURL = "/v2/views";
const updateDocumentViewURL = "v2/views/{view_id}";
const deleteDocumentViewURL = "v2/views/{view_id}";

const bulkActionURL = "/v2/async/operation";
const sendDocumentApprovalRequestURL = "/v3/emails/send/approval/request";
const sendDocumentApprovalVerdictURL = "/v3/emails/send/approval/verdict";
const fetchDocumentApprovalStatusURL =
	"/{api_version}/automation/approval/details/:id";
const downloadWonDocumentUrl = "/v2/documents/download_url/{document_id}";
const downloadDocumentLogUrl = "v2/documents/{document_id}/downloadAttachments";
const sendDocumentPinURL = "/v2/document/pin/send";
const verifyDocumentPinURL = "/v2/document/pin/approve";
const updateDocumentPoDetails =
	"/v2/documents/{document_id}/update-po-details/";
const bulkAssignSignatureURL = "/v2/documents/bulk/assign/:id";
const getPaymentSummaryURL = "/v2/documents/:id/payment_summary";
const createCheckoutSessionURL =
	"/v2/documents/:id/payment/create_checkout_session";

const getQuickbooksSyncFormDataURL =
	"v2/form/quickbooks/form_data/?document_id={document_id}";
const quickbooksFormDataSyncURL = "v2/documents/{document_id}/quickbooks/sync";
const getQuickbooksCustomersURL =
	"v2/form/quickbooks/customers/?customer_name_like={searchString}";
const quickbooksCreateCustomerURL =
	"/v2/companies/integrations/quickbooks/create_customer/";
const expireStripeCheckoutSessionURL =
	"v2/documents/:id/payment/expire-checkout-session";
const expirePaymentsCheckoutSessionURL =
	"v2/documents/:id/payment/expire-payments-session/";

const updateOrderFormPublishStatusURL =
	"/v2/documents/{document_id}/order-form-status";
const submitGenericOrderFormURL =
	"/v2/documents/{document_id}/submit-generic-order-form";
const submitSpecificOrderFormURL =
	"/v2/documents/{document_id}/submit-specific-order-form";

const orderFormRequestorDetailsToClientDetailsURL =
	"/v2/documents/order-form/entity/{document_id}";

const updateDocumentLinkStatusURL = "/v2/documents/sharing-link/{document_id}/";
const sendOpenAIQueryURL = "/v2/openai/text/completion";
const fetchDocumentParamaters = "/v2/documents/fetch-parameters";

const getOpenAIQueryHistoryURL = "/v2/openai/queries";
const signDocumentURL = "/v2/documents/{document_id}/sign";
const startAIWriterTrailURL = "/v2/companies/enable-trial-for-ai-writer";

const editBlockUrl = "/v2/blocks/{block_id}";
const syncCurrencyRateUrl = "/v2/documents/{document_id}/sync-currency-rate/";

const getDefaultChildEnities = () => {
	return [
		ChildEntityHydrationParams.BLOCKS_CURRENT_REVISION,
		ChildEntityHydrationParams.COMPANIES_ALL,
		ChildEntityHydrationParams.TAX
	];
};

export const getDocuments = async (
	documentsRequest: DocumentsRequest
): Promise<DocumentsResponse> => {
	try {
		let url = getDocumentsUrl;
		const includedChildEntities = includeChildEntities(
			documentsRequest.includeEntities
		);
		if (documentsRequest?.filters?.length > 0) {
			const appliedFilters = getTableQueryFilters(
				documentsRequest.filters,
				false
			);
			url =
				url +
				"?" +
				appliedFilters +
				(includedChildEntities && includedChildEntities?.length > 0
					? `&${includedChildEntities}`
					: "") +
				(documentsRequest?.keywords &&
				documentsRequest.keywords?.length > 0
					? `&q=${documentsRequest.keywords}`
					: "");
		} else {
			url =
				url +
				(includedChildEntities && includedChildEntities?.length > 0
					? "?" + includedChildEntities
					: "") +
				(documentsRequest?.keywords &&
				documentsRequest.keywords?.length > 0
					? `?q=${documentsRequest.keywords}`
					: "");
		}
		if (documentsRequest.sortQuery) {
			url += `&${documentsRequest.sortQuery}`;
		}
		if (documentsRequest?.page) {
			url += `&page=${documentsRequest.page}`;
		}
		if (documentsRequest?.limit) {
			url += `&limit=${documentsRequest.limit}`;
		}
		const response: AxiosResponse<DocumentsResponse> = await axios(
			getRequest(url, null, true, false, false)
		);

		return {
			...response.data,
			data: (response.data.data ?? []).map(
				withNormalizedDocumentCurrencyConfig
			)
		};
	} catch (error) {
		return Promise.reject(error);
	}
};

export const deleteDocument = async (
	document: Document
): Promise<DeletedRecord> => {
	try {
		const response: AxiosResponse<DeletedRecord> = await axios(
			deleteRequest(
				deleteDocumentUrl.replace("{document_id}", document.id),
				null,
				true,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const addDocument = async (
	addDocumentRequest: AddDocumentRequest
): Promise<Document> => {
	try {
		const response: AxiosResponse<Document> = await axios(
			postRequest(addDocumentUrl, addDocumentRequest, true, false, false)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editDocument = async (
	editDocumentRequest: EditDocumentRequest
): Promise<Document> => {
	let queryParams = "";
	// Add included_child_entities if present to query params
	if (editDocumentRequest?.included_child_entities?.length > 0) {
		const included_child_entities = includeChildEntities(
			editDocumentRequest.included_child_entities
		);
		queryParams = `${
			queryParams && queryParams?.length > 0
				? `${queryParams}&${included_child_entities}`
				: included_child_entities
		}`;
	}
	const editDocumentUrlWithDocumentId = editDocumentUrl.replace(
		"{document_id}",
		editDocumentRequest.document.id
	);

	const { ...documentRequest } = editDocumentRequest.document;

	try {
		const response: AxiosResponse<Document> = await axios(
			putRequest(
				`${editDocumentUrlWithDocumentId}${
					queryParams.length > 0 ? `?${queryParams}` : ""
				}`,
				documentRequest,
				true,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editRevision = async (
	documentId: string,
	revisionData: DocumentRevision
): Promise<DocumentRevision> => {
	delete revisionData.blocks;
	try {
		const response: AxiosResponse<DocumentRevision> = await axios(
			putRequest(
				`${editRevisionUrl
					.replace("{document_id}", documentId)
					.replace("{revision_id}", revisionData.revision_id)}`,
				revisionData,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateDocumentPageDetails = async (
	documentId: string,
	pageDetails: DocumentPageDetails
): Promise<Document> => {
	try {
		const response: AxiosResponse<Document> = await axios(
			putRequest(
				updateDocumentPageDetailsUrl.replace(
					"{document_id}",
					documentId
				),
				{ page_detail: pageDetails },
				true,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const downloadDocument = async (
	downloadDocumentRequest: Document,
	shouldIncludeToZipParam?: boolean
): Promise<Blob> => {
	try {
		const queryParamAuthDetails = getQueryParamAuthDetails();
		const queryParam = queryParamAuthDetails?.queryParam
			? queryParamAuthDetails.queryParam
			: "";

		let url = downloadDocumentUrl.replace(
			"{document_id}",
			downloadDocumentRequest.id
		);
		if (shouldIncludeToZipParam) url += `?${queryParam}&toZip=false`;
		else url += `?${queryParam}`;

		const customHeaders = getDocumentPinCache(downloadDocumentRequest.id);

		const response: AxiosResponse<Blob> = await axios(
			getRequest(url, null, true, false, true, undefined, customHeaders)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const downloadDocumentFromClient = async (
	downloadDocumentFromClientRequest: Document
): Promise<Blob> => {
	try {
		const queryParamAuthDetails = getQueryParamAuthDetails();
		if (!queryParamAuthDetails) {
			throw new Error("This is not a valid url");
		}

		const customHeaders = getDocumentPinCache(
			downloadDocumentFromClientRequest.id
		);

		const response: AxiosResponse<Blob> = await axios(
			getRequest(
				downloadDocumentUrl.replace(
					"{document_id}",
					downloadDocumentFromClientRequest.id
				) +
					"?" +
					queryParamAuthDetails.queryParam,
				null,
				true,
				false,
				true,
				undefined,
				customHeaders
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getDocumentDetails = async (
	documentId: string,
	appendQueryParamAuth?: boolean,
	skipViewTracking?: boolean
): Promise<Document> => {
	try {
		const includedChildEntities = includeChildEntities(
			getDefaultChildEnities()
		);
		let url = getDocumentDetailsUrl.replace("{document_id}", documentId);

		const authQueryParam = getQueryParamAuthDetails();
		if (appendQueryParamAuth && !!authQueryParam) {
			url =
				url +
				"?" +
				authQueryParam.queryParam +
				"&" +
				includeChildEntities;
		} else {
			url = url + "?" + includedChildEntities;
		}
		if (skipViewTracking) {
			url = url + "&skip_view_tracking=true";
		}

		const response: AxiosResponse<Document> = await axios(
			getRequest(url, null, true, false, false)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateForceCancelSignatureInDoc = async (
	documentId: string
): Promise<Document> => {
	try {
		const url = cancelSignatureDocumentUrl.replace(
			"{document_id}",
			documentId
		);

		const response: AxiosResponse<Document> = await axios(
			putRequest(url, {}, true, false, false)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const enableDocumentEditing = async (
	documentId: string
): Promise<Document> => {
	try {
		const url = enableEditingUrl.replace("{document_id}", documentId);
		const response: AxiosResponse<Document> = await axios(
			putRequest(url, {}, true, false, false)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const enableClientViewing = async (
	documentId: string
): Promise<Document> => {
	try {
		const url = enableClientViewingUrl.replace("{document_id}", documentId);
		const response: AxiosResponse<Document> = await axios(
			putRequest(url, {}, true, false, false)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getMailRecipients = async (): Promise<MailRecipientResponse> => {
	try {
		const response: AxiosResponse<MailRecipientResponse> = await axios(
			getRequest(getMailRecipientsUrl, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const approveDocument = async (documentId: string): Promise<any> => {
	try {
		const queryParamAuthDetails = getQueryParamAuthDetails();
		const queryParam = queryParamAuthDetails
			? "?" + queryParamAuthDetails.queryParam + "&"
			: "?";

		const customHeaders = getDocumentPinCache(documentId);

		const response: AxiosResponse<any> = await axios(
			putRequest(
				approveDocumentUrl.replace("{document_id}", documentId) +
					queryParam +
					includeChildEntities(getDefaultChildEnities()),
				{},
				true,
				false,
				false,
				customHeaders
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateDocumentPaymentSettings = async (
	documentId: string,
	paymentData: DocumentPayment
): Promise<Document> => {
	try {
		const response: AxiosResponse<Document> = await axios(
			putRequest(
				setDocumentPaymentSettings.replace("{document_id}", documentId),
				paymentData,
				true,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const startConnectBoosterPayment = async (
	documentId: string,
	mode: string
): Promise<Document> => {
	try {
		const queryParamAuthDetails = getQueryParamAuthDetails();
		const queryParam = queryParamAuthDetails
			? "?" + queryParamAuthDetails.queryParam
			: "?";
		const startPaymentURL =
			mode === "pay"
				? startPayment.replace("{document_id}", documentId) + queryParam
				: startPayment.replace("{document_id}", documentId) +
				  queryParam +
				  "&opt_out=true";

		const customHeaders = getDocumentPinCache(documentId);

		const response: AxiosResponse<Document> = await axios(
			putRequest(startPaymentURL, {}, true, false, false, customHeaders)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const makeConnectBoosterPayment = async (
	documentId: string,
	connectBoosterPaymentRequest: ConnectBoosterPaymentRequest,
	optOut: boolean
): Promise<Document> => {
	try {
		const queryParamAuthDetails = getQueryParamAuthDetails();
		const queryParam = queryParamAuthDetails
			? "?" + queryParamAuthDetails.queryParam
			: "?";
		const startPaymentURL =
			startPayment.replace("{document_id}", documentId) +
			queryParam +
			(optOut ? "&opt_out=true" : "");

		const customHeaders = getDocumentPinCache(documentId);
		const response: AxiosResponse<Document> = await axios(
			putRequest(
				startPaymentURL,
				connectBoosterPaymentRequest,
				true,
				false,
				false,
				customHeaders
			)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (err) {
		return Promise.reject(err);
	}
};

export const updateClientPaymentDetails = async (
	documentId: string,
	modifiedData: ClientPaymentData
): Promise<any> => {
	try {
		const queryParamAuthDetails = getQueryParamAuthDetails();
		const queryParam = queryParamAuthDetails
			? "?" + queryParamAuthDetails.queryParam
			: "?";
		const updatePaymentURL =
			updateClientPaymentSettings.replace("{document_id}", documentId) +
			queryParam;

		const customHeaders = getDocumentPinCache(documentId);
		const response: AxiosResponse<any> = await axios(
			putRequest(
				updatePaymentURL,
				modifiedData,
				true,
				false,
				false,
				customHeaders
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const cbPayment = async (
	// is generated
	connect_booster_payment_url: string
): Promise<any> => {
	try {
		const response: AxiosResponse<any> = await axios(
			getRequest(connect_booster_payment_url, null, false, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateRevisionBlockList = async ({
	documentId,
	blockList
}: {
	documentId: string;
	blockList: string[];
}): Promise<Document> => {
	try {
		const response: AxiosResponse<Document> = await axios(
			putRequest(
				editRevisionBlockListUrl.replace("{document_id}", documentId) +
					"?" +
					includeChildEntities(getDefaultChildEnities()),
				{ block_list: blockList },
				true,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getDocumentDetailsFromClient = async (
	documentId: string,
	isGOF = false,
	skipViewTracking?: boolean
): Promise<Document> => {
	try {
		let queryParamAuthDetails = getQueryParamAuthDetails();

		const includedChildEntities = includeChildEntities(
			getDefaultChildEnities()
		);

		const url = getDocumentDetailsUrl.replace("{document_id}", documentId);

		const extraRequestParams: string[] = [];
		if (queryParamAuthDetails) {
			if (
				isGOF &&
				queryParamAuthDetails.queryParam?.includes(
					QueryParamConstants.CLIENT_USER_ID
				)
			) {
				queryParamAuthDetails = new QueryParamCredentials(
					UserType.ClientUser,
					"unknown"
				);
			}
			extraRequestParams.push(queryParamAuthDetails.queryParam);
		}
		extraRequestParams.push(includedChildEntities);
		if (skipViewTracking) {
			extraRequestParams.push("skip_view_tracking=true");
		}

		const customHeaders = getDocumentPinCache(documentId);

		const response: AxiosResponse<Document> = await axios(
			getRequest(
				[url, extraRequestParams.join("&")].join("?"),
				null,
				false,
				false,
				false,
				undefined,
				{
					...customHeaders,
					"Skip-Server-Error-Banner-Status-Codes": "400"
				}
			)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};
export const cancelSignatureFromClient = async (
	documentId: string
): Promise<Document> => {
	try {
		const queryParamAuthDetails = getQueryParamAuthDetails();
		const queryParam = queryParamAuthDetails
			? "?" + queryParamAuthDetails.queryParam + "&"
			: "?";

		const includedChildEntities = includeChildEntities(
			getDefaultChildEnities()
		);

		const customHeaders = getDocumentPinCache(documentId);

		const url =
			cancelSignatureDocumentUrl.replace("{document_id}", documentId) +
			queryParam +
			includedChildEntities;

		const response: AxiosResponse<Document> = await axios(
			putRequest(url, {}, true, false, false, customHeaders)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};
export const assignSignatureBlockToRecipient = async ({
	documentId,
	blockId,
	signerId
}: {
	documentId: string;
	blockId: string;
	signerId?: string;
}): Promise<Document> => {
	try {
		const response: AxiosResponse<Document> = await axios(
			putRequest(
				assignSignatureBlockToRecipientUrl.replace(
					"{document_id}",
					documentId
				) +
					"?" +
					includeChildEntities(getDefaultChildEnities()),
				{ block_id: blockId, signer_id: signerId },
				true,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};
export const reorderSigners = async ({
	documentId,
	signerIds
}: {
	documentId: string;
	signerIds: string[];
}): Promise<Document> => {
	try {
		const response: AxiosResponse<Document> = await axios(
			putRequest(
				reorderSignersUrl.replace("{document_id}", documentId),
				{ signer_ids: signerIds },
				true,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const renewCurrentActiveDocument = async (
	documentId: string,
	newExpiryDetails: DocumentExpiry
): Promise<Document> => {
	try {
		const response: AxiosResponse<Document> = await axios(
			putRequest(
				renewDocumentUrl.replace("{document_id}", documentId),
				{ document_expiry_details: newExpiryDetails },
				true,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getEmptyMergeTags = async (
	documentId: string
): Promise<EmptyMergeTagResponse> => {
	try {
		const response: AxiosResponse<EmptyMergeTagResponse> = await axios(
			getRequest(
				getEmptyMergeTagsUrl.replace("{document_id}", documentId),
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const validateAutotaskItems = async (
	documentId: string
): Promise<AutotaskProductValidationView[]> => {
	try {
		const response: AxiosResponse<AutotaskProductValidationView[]> =
			await axios(
				getRequest(
					validateAutotaskItemsURL.replace(
						"{document_id}",
						documentId
					),
					null,
					true,
					false,
					false
				)
			);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const downloadDocumentProductList = async (
	downloadDocumentProductListRequest: DownloadDocumentProductListRequest
): Promise<Blob> => {
	try {
		const documentId = downloadDocumentProductListRequest.documentId;
		const revisionId = downloadDocumentProductListRequest.revisionId;
		const response: AxiosResponse<Blob> = await axios(
			getRequest(
				downloadDocumentProductListUrl
					.replace("{document_id}", documentId)
					.replace("{revision_id}", revisionId),
				null,
				true,
				false,
				true
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getDocumentPaymentStatusCB = async (
	paymentData: string
): Promise<Document> => {
	try {
		const response: AxiosResponse<Document> = await axios(
			getRequest(
				getDocumentPaymentStatus + paymentData,
				null,
				false,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const updateMergeTagsByValue = async (
	mergeTagInput: MergeTagInput
): Promise<MergeTagValueUpdateResponse> => {
	try {
		const response: AxiosResponse<MergeTagValueUpdateResponse> =
			await axios(
				postRequest(
					updateMergeTagValues,
					mergeTagInput,
					true,
					false,
					false
				)
			);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const documentBulkUpdate = async (
	documentStatusUpdateRequest: DocumentBulkUpdateStatusRequest
): Promise<DocumentBulkUpdateStatusRequest> => {
	try {
		const response: AxiosResponse<DocumentBulkUpdateStatusRequest> =
			await axios(
				postRequest(
					documentBulkUpdateUrl,
					documentStatusUpdateRequest,
					true,
					false,
					false
				)
			);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const pushChangesToAutotask = async (
	documentId: string,
	pushChangesToAutotaskRequest: DynamicFormCreateRequest
): Promise<void> => {
	try {
		await axios(
			putRequest(
				postPushChangesToAutotask.replace(":id", documentId),
				pushChangesToAutotaskRequest,
				true,
				false,
				false
			)
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getDocumentViewMetadata =
	async (): Promise<CustomViewsMetadata> => {
		try {
			const response: AxiosResponse<CustomViewsMetadata> = await axios(
				getRequest(
					getDocumentViewMetadataURL.replace(
						"{entity_type}",
						ZomentumEntities.Document
					),
					null,
					true,
					false,
					false
				)
			);
			return response.data;
		} catch (error) {
			console.error(error);
			return Promise.reject(error);
		}
	};

export const getAllDocumentViews = async (): Promise<CustomViewResponse> => {
	try {
		const response: AxiosResponse<CustomViewResponse> = await axios(
			getRequest(
				getAllDocumentViewURL.replace(
					"{entity_type}",
					ZomentumEntities.Document
				),
				null,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const getDocumentListPost = async (
	documentRequest: ListPagePostRequest
): Promise<DocumentsResponse> => {
	try {
		const includedChildEntities = includeChildEntities(
			documentRequest.included_child_entities
		);
		const response: AxiosResponse<DocumentsResponse> = await axios(
			postRequest(
				getDocumentListPostURL + `?${includedChildEntities}`,
				documentRequest,
				true,
				false,
				false
			)
		);

		return {
			...response.data,
			data: (response.data.data ?? []).map(
				withNormalizedDocumentCurrencyConfig
			)
		};
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const createDocumentView = async (
	customViewRequest: CustomView
): Promise<CustomView> => {
	try {
		const response: AxiosResponse<CustomView> = await axios(
			postRequest(
				createDocumentViewURL,
				customViewRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const updateDocumentView = async (
	customViewRequest: CustomView
): Promise<CustomView> => {
	try {
		const response: AxiosResponse<CustomView> = await axios(
			putRequest(
				updateDocumentViewURL.replace(
					"{view_id}",
					customViewRequest.id
				),
				customViewRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const deleteDocumentView = async (
	customViewId: string
): Promise<unknown> => {
	try {
		const response: AxiosResponse<unknown> = await axios(
			deleteRequest(
				deleteDocumentViewURL.replace("{view_id}", customViewId),
				null,
				true,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const bulkDeleteDocumentRecords = async (
	bulkDeleteRecordsRequest: BulkDeleteRecordsRequest
): Promise<any> => {
	// fix the type
	try {
		const response: AxiosResponse<any> = await axios(
			postRequest(
				bulkActionURL,
				bulkDeleteRecordsRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const bulkUpdateRecords = async (
	bulkUpdateRecordsRequest: BulkUpdateRecordsRequest
): Promise<any> => {
	// fix the type
	try {
		const response: AxiosResponse<any> = await axios(
			postRequest(
				bulkActionURL,
				bulkUpdateRecordsRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const sendDocumentApprovalRequest = async (
	approvalRequest: DocumentApprovalSendRequest
): Promise<ApprovalDetails> => {
	try {
		const response: AxiosResponse<ApprovalDetails> = await axios(
			postRequest(
				sendDocumentApprovalRequestURL,
				approvalRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};
export const sendDocumentApprovalVerdict = async (
	approvalVerdictRequest: DocumentApprovalVerdictRequest
): Promise<ApprovalDetails> => {
	try {
		const response: AxiosResponse<ApprovalDetails> = await axios(
			postRequest(
				sendDocumentApprovalVerdictURL,
				approvalVerdictRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchDocumentApprovalStatus = async (
	documentId: string,
	apiVersion = "v2"
): Promise<ApprovalStatusResponse> => {
	try {
		const response: AxiosResponse<ApprovalStatusResponse> = await axios(
			getRequest(
				fetchDocumentApprovalStatusURL
					.replace("{api_version}", apiVersion)
					.replace(":id", documentId),
				null,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};
export const downloadWonDocument = async (
	documentId: string,
	forSigning?: boolean
): Promise<DocumentWonUrlResponse> => {
	try {
		const customHeaders = getDocumentPinCache(documentId);
		const queryParamAuthDetails = getQueryParamAuthDetails();
		const queryParts: string[] = [];
		if (queryParamAuthDetails) {
			queryParts.push(queryParamAuthDetails.queryParam);
		}
		if (forSigning) {
			queryParts.push("for_signing=true");
		}
		const queryParam = queryParts.length ? "?" + queryParts.join("&") : "";

		const response: AxiosResponse<DocumentWonUrlResponse> = await axios(
			getRequest(
				`${downloadWonDocumentUrl.replace(
					"{document_id}",
					documentId
				)}${queryParam}`,
				null,
				true,
				false,
				false,
				undefined,
				{
					...customHeaders,
					"Skip-Server-Error-Banner-Status-Codes": "504"
				}
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const downloadDocumentLogs = async (
	documentId: string,
	logs: string[]
): Promise<{ fileData: Blob; fileName?: string }> => {
	try {
		const queryParam = logs.map(log => `${log}=true`).join("&");
		const response: AxiosResponse<Blob> = await axios(
			getRequest(
				`${downloadDocumentLogUrl.replace(
					"{document_id}",
					documentId
				)}?${queryParam}`,
				null,
				true,
				false,
				true
			)
		);
		const contentDisposition = response.headers[
			"content-disposition"
		] as string;
		const fileName = getFileNameFromContentDisposition(contentDisposition);
		return { fileData: response.data, fileName };
	} catch (error) {
		return Promise.reject(error);
	}
};

export const sendDocumentPin = async (
	documentPinGenerationRequest: DocumentPinGenerationRequest
): Promise<DocumentPinGenerationResponse> => {
	try {
		const queryParams = getQueryParamAuthDetails();

		const response: AxiosResponse<DocumentPinGenerationResponse> =
			await axios(
				postRequest(
					sendDocumentPinURL + "?" + queryParams?.queryParam,
					documentPinGenerationRequest,
					true,
					false,
					false
				)
			);

		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};
export const verifyDocumentPin = async (
	verifyDocumentPinRequest: VerifyDocumentPinRequest
): Promise<VerifyDocumentPinResponse> => {
	try {
		const queryParams = getQueryParamAuthDetails();

		const response: AxiosResponse<VerifyDocumentPinResponse> = await axios(
			postRequest(
				verifyDocumentPinURL + "?" + queryParams?.queryParam,
				verifyDocumentPinRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updatePoDetails = async (
	poDetails: UpdatePoDetailsPayload
): Promise<UpdatePoDetailsResponse> => {
	try {
		const queryParamAuthDetails = getQueryParamAuthDetails();
		const queryParam = queryParamAuthDetails
			? "?" + queryParamAuthDetails.queryParam
			: "";
		const updatePoDetailsUrl =
			updateDocumentPoDetails.replace(
				"{document_id}",
				poDetails.document_id
			) + queryParam;

		const customHeaders = getDocumentPinCache(poDetails.document_id);

		const response: AxiosResponse<UpdatePoDetailsResponse> = await axios(
			putRequest(
				updatePoDetailsUrl,
				poDetails,
				true,
				false,
				false,
				customHeaders
			)
		);
		return response.data;
	} catch (err) {
		return Promise.reject(err);
	}
};

export const bulkAssignSignatureBlocks = async (
	bulkAssignSignatureBlocksRequest: BulkAssignSignatureBlocksRequest,
	documentId: string
): Promise<Document> => {
	try {
		const response: AxiosResponse<Document> = await axios(
			putRequest(
				bulkAssignSignatureURL.replace(":id", documentId),
				bulkAssignSignatureBlocksRequest,
				true,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (err) {
		return Promise.reject(err);
	}
};

export const getPaymentDetails = async (
	documentId: string
): Promise<PaymentSummary> => {
	try {
		const response: AxiosResponse<PaymentSummary> = await axios(
			getRequest(
				getPaymentSummaryURL.replace(":id", documentId),
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (err) {
		return Promise.reject(err);
	}
};

export const createCheckoutSession = async (
	discountId: string,
	paymentMethod: PaymentMethod,
	gateway: Gateway
): Promise<string> => {
	try {
		const queryParamAuthDetails = getQueryParamAuthDetails();
		const queryParam = queryParamAuthDetails
			? "?" + queryParamAuthDetails.queryParam
			: "?";

		const requestURL =
			createCheckoutSessionURL.replace(":id", discountId) + queryParam;

		const response: AxiosResponse<{ session: string }> = await axios(
			postRequest(
				requestURL,
				{
					payment_method: paymentMethod,
					gateway
				},
				false,
				false,
				false
			)
		);
		return response.data.session;
	} catch (err) {
		return Promise.reject(err);
	}
};
export const getQuickbooksSyncMetadata = async (
	documentId: string
): Promise<QuickbooksDocumentSyncFormData> => {
	try {
		const response: AxiosResponse<QuickbooksDocumentSyncFormData> =
			await axios(
				getRequest(
					getQuickbooksSyncFormDataURL.replace(
						"{document_id}",
						documentId
					),
					null,
					true,
					false,
					false
				)
			);
		return response.data;
	} catch (err) {
		return Promise.reject(err);
	}
};

export const quickbooksFormSyncData = async (
	payload: QuickbooksSyncFormDataParams
): Promise<Record<string, unknown>> => {
	try {
		const response: AxiosResponse<Record<string, unknown>> = await axios(
			putRequest(
				quickbooksFormDataSyncURL.replace(
					"{document_id}",
					payload.documentId
				),
				payload.formValues,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const createQuickbooksCustomer = async (
	createCustomerRequest: QuickbooksCreateCustomerRequest
): Promise<CommonNameValuePair> => {
	try {
		const response: AxiosResponse<CommonNameValuePair> = await axios(
			postRequest(
				quickbooksCreateCustomerURL,
				createCustomerRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (err) {
		return Promise.reject(err);
	}
};

export const getQuickbooksCustomers = async (
	searchString: string
): Promise<QuickbooksCustomerResponse> => {
	try {
		const response: AxiosResponse<QuickbooksCustomerResponse> = await axios(
			getRequest(
				getQuickbooksCustomersURL.replace(
					"{searchString}",
					searchString
				),
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (err) {
		return Promise.reject(err);
	}
};

export const expireCurrentStripeCheckoutSession = async (
	documentId: string
): Promise<{ message: string }> => {
	try {
		const queryParamAuthDetails = getQueryParamAuthDetails();
		const queryParam = queryParamAuthDetails
			? "?" + queryParamAuthDetails.queryParam
			: "";
		const response: AxiosResponse<{ message: string }> = await axios(
			putRequest(
				expireStripeCheckoutSessionURL.replace(":id", documentId) +
					queryParam,
				null,
				false,
				false,
				false
			)
		);
		return response.data;
	} catch (err) {
		return Promise.reject(err);
	}
};

export const expireCurrentZMPaymentsCheckoutSession = async (
	documentId: string
): Promise<{ message: string }> => {
	try {
		const queryParamAuthDetails = getQueryParamAuthDetails();
		const queryParam = queryParamAuthDetails
			? "?" + queryParamAuthDetails.queryParam
			: "";
		const response: AxiosResponse<{ message: string }> = await axios(
			putRequest(
				expirePaymentsCheckoutSessionURL.replace(":id", documentId) +
					queryParam,
				null,
				false,
				false,
				false
			)
		);
		return response.data;
	} catch (err) {
		return Promise.reject(err);
	}
};

export const updateOrderFormPublishStatus = async (
	updateOrderFormPublishStatusRequest: UpdateOrderFormPublishStatusRequest,
	documentId: string
): Promise<Document> => {
	try {
		const response: AxiosResponse<Document> = await axios(
			putRequest(
				updateOrderFormPublishStatusURL.replace(
					"{document_id}",
					documentId
				),
				updateOrderFormPublishStatusRequest,
				true,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (err) {
		return Promise.reject(err);
	}
};

export const submitGenericOrderForm = async (
	submitGenericOrderFormRequest: SubmitOrderFormRequest,
	documentId: string
): Promise<Document> => {
	try {
		const response: AxiosResponse<Document> = await axios(
			putRequest(
				submitGenericOrderFormURL.replace("{document_id}", documentId),
				submitGenericOrderFormRequest,
				true,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (err) {
		return Promise.reject(err);
	}
};

export const submitSpecificOrderForm = async (
	documentId: string,
	poNumber: string
): Promise<Document> => {
	try {
		const queryParamAuthDetails = getQueryParamAuthDetails();
		const queryParam = queryParamAuthDetails
			? "?" + queryParamAuthDetails.queryParam
			: "";
		const requestPayload = poNumber?.length ? { po_number: poNumber } : {};
		const response: AxiosResponse<Document> = await axios(
			putRequest(
				submitSpecificOrderFormURL.replace(
					"{document_id}",
					documentId
				) + queryParam,
				requestPayload,
				true,
				false,
				false
			)
		);

		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (err) {
		return Promise.reject(err);
	}
};

export const orderFormRequestorDetailsToClientDetails = async (
	orderFormRequestorDetailsToClientDetailsRequest: OrderFormRequestorDetailsToClientDetailsRequest,
	documentId: string
): Promise<Document> => {
	try {
		const response: AxiosResponse<Document> = await axios(
			putRequest(
				orderFormRequestorDetailsToClientDetailsURL.replace(
					"{document_id}",
					documentId
				),
				orderFormRequestorDetailsToClientDetailsRequest,
				true,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (err) {
		return Promise.reject(err);
	}
};

export const updateDocumentStatusLink = async (
	updateDocumentLinkStatusRequest: UpdateDocumentLinkStatusRequest,
	documentId: string
): Promise<Document> => {
	try {
		const response: AxiosResponse<Document> = await axios(
			putRequest(
				updateDocumentLinkStatusURL.replace(
					"{document_id}",
					documentId
				),
				updateDocumentLinkStatusRequest,
				true,
				false,
				false
			)
		);

		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (err) {
		return Promise.reject(err);
	}
};

export const sendOpenAiPrompt = async (
	prompt: Record<string, string>
): Promise<OpenAiResponse> => {
	try {
		const response: AxiosResponse<OpenAiResponse> = await axios(
			postRequest(sendOpenAIQueryURL, prompt, true, false, false)
		);

		return response.data;
	} catch (err) {
		return Promise.reject(err);
	}
};

export const getOpenAIQueryHistory =
	async (): Promise<OpenAIQueryHistoryResponse> => {
		try {
			const response: AxiosResponse<OpenAIQueryHistoryResponse> =
				await axios(
					getRequest(
						getOpenAIQueryHistoryURL,
						null,
						true,
						false,
						false
					)
				);

			return response.data;
		} catch (err) {
			return Promise.reject(err);
		}
	};

export const requestAIWriterTrail =
	async (): Promise<AIWriterTrailResponse> => {
		try {
			const response: AxiosResponse<AIWriterTrailResponse> = await axios(
				putRequest(startAIWriterTrailURL, {}, true, false, false)
			);

			return response.data;
		} catch (err) {
			return Promise.reject(err);
		}
	};

export const sendDocumentForSignature = async ({
	documentId,
	clientUserId,
	userId,
	pdfBlob
}: SendDocumentForSignatureRequest): Promise<Blob> => {
	try {
		const formData = new FormData();
		const fileName = `pdf_${documentId}.pdf`;
		const file = new File([pdfBlob], fileName, { type: "application/pdf" });
		formData.append("file", file, fileName);
		const url = `${signDocumentURL.replace("{document_id}", documentId)}${
			clientUserId
				? `?client_user_id=${clientUserId}`
				: userId
				? `?user_id=${userId}`
				: ""
		}`;

		const customHeaders = getDocumentPinCache(documentId);
		const requestOptions = postRequest(url, formData, true, false, true);

		const response: AxiosResponse<string> = await axios({
			...requestOptions,
			responseType: "blob",
			headers: {
				...requestOptions.headers,
				...customHeaders
			}
		});
		const blob = new Blob([response.data], { type: "application/pdf" });
		return blob;
	} catch (err) {
		return Promise.reject(err);
	}
};

export const getDocumentParamaters = async (
	query: string
): Promise<AIDocumentCreateResponse> => {
	try {
		const response: AxiosResponse<AIDocumentCreateResponse> = await axios(
			postRequest(
				fetchDocumentParamaters,
				{
					prompt: query
				},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (err) {
		return Promise.reject(err);
	}
};

export const getRecentDocuments = async (
	request: RecentDocumentRequest
): Promise<RecentDocumentResponse> => {
	const hasIds =
		(request.opportunity_ids?.length ?? 0) > 0 ||
		(request.client_company_ids?.length ?? 0) > 0 ||
		(request.client_user_ids?.length ?? 0) > 0;
	if (!hasIds) {
		return { data: [], filtered_count_map: null };
	}
	try {
		const response: AxiosResponse<RecentDocumentResponse> = await axios(
			postRequest(recentDocumentURL, request, true, false, false)
		);
		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const getRecentDocumentsByOpportunityIds = (
	opportunityIds: string[]
): Promise<RecentDocumentResponse> =>
	getRecentDocuments({ opportunity_ids: opportunityIds });

export const getRecentDocumentsByClientCompanyIds = (
	clientCompanyIds: string[]
): Promise<RecentDocumentResponse> =>
	getRecentDocuments({ client_company_ids: clientCompanyIds });

export const getRecentDocumentsByClientUserIds = (
	clientUserIds: string[]
): Promise<RecentDocumentResponse> =>
	getRecentDocuments({ client_user_ids: clientUserIds });

// update block
export const editBlock = async (block: Block): Promise<BlockResponse> => {
	try {
		const response: AxiosResponse<BlockResponse> = await axios(
			putRequest(
				editBlockUrl.replace("{block_id}", block.id),
				block,
				true,
				false,
				false
			)
		);
		return deepFreeze(response.data);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const syncDocumentCurrencyRate = async (
	documentId: string
): Promise<Document> => {
	try {
		const response: AxiosResponse<Document> = await axios(
			postRequest(
				syncCurrencyRateUrl.replace("{document_id}", documentId),
				null,
				true,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};
